import {
  Mesh,
  VertexData,
  StandardMaterial,
} from '@babylonjs/core';
import { Chunk, CHUNK_SIZE, CHUNK_HEIGHT } from './Chunk';
import { Block } from './Block';
import { BlockRegistry } from './BlockRegistry';

interface FaceData {
  positions: number[];
  normals: number[];
  indices: number[];
  colors: number[];
}

type GetBlockFunc = (x: number, y: number, z: number) => number;

export class VoxelMesher {
  private static registry = BlockRegistry.getInstance();

  // Face directions: [dx, dy, dz, normalIndex]
  private static readonly FACES = [
    { dir: [0, 1, 0], name: 'top' },     // Top (+Y)
    { dir: [0, -1, 0], name: 'bottom' }, // Bottom (-Y)
    { dir: [0, 0, 1], name: 'north' },   // North (+Z)
    { dir: [0, 0, -1], name: 'south' },  // South (-Z)
    { dir: [1, 0, 0], name: 'east' },    // East (+X)
    { dir: [-1, 0, 0], name: 'west' },   // West (-X)
  ];

  // Vertex positions for each face (relative to block origin)
  private static readonly FACE_VERTICES: { [key: string]: number[][] } = {
    top: [
      [0, 1, 0], [1, 1, 0], [1, 1, 1], [0, 1, 1]
    ],
    bottom: [
      [0, 0, 1], [1, 0, 1], [1, 0, 0], [0, 0, 0]
    ],
    north: [
      [1, 0, 1], [0, 0, 1], [0, 1, 1], [1, 1, 1]
    ],
    south: [
      [0, 0, 0], [1, 0, 0], [1, 1, 0], [0, 1, 0]
    ],
    east: [
      [1, 0, 0], [1, 0, 1], [1, 1, 1], [1, 1, 0]
    ],
    west: [
      [0, 0, 1], [0, 0, 0], [0, 1, 0], [0, 1, 1]
    ],
  };

  // Normals for each face
  private static readonly FACE_NORMALS: { [key: string]: number[] } = {
    top: [0, 1, 0],
    bottom: [0, -1, 0],
    north: [0, 0, 1],
    south: [0, 0, -1],
    east: [1, 0, 0],
    west: [-1, 0, 0],
  };

  public static buildMesh(chunk: Chunk, getNeighborBlock?: GetBlockFunc): Mesh | null {
    const faceData: FaceData = {
      positions: [],
      normals: [],
      indices: [],
      colors: [],
    };

    let vertexCount = 0;

    // Helper to get block, checking neighbors if needed
    const getBlock = (x: number, y: number, z: number): number => {
      if (x >= 0 && x < CHUNK_SIZE && y >= 0 && y < CHUNK_HEIGHT && z >= 0 && z < CHUNK_SIZE) {
        return chunk.getBlock(x, y, z);
      }
      if (getNeighborBlock) {
        const worldX = chunk.worldPosition.x + x;
        const worldZ = chunk.worldPosition.z + z;
        return getNeighborBlock(worldX, y, worldZ);
      }
      return Block.AIR;
    };

    // Iterate through all blocks in the chunk
    for (let y = 0; y < CHUNK_HEIGHT; y++) {
      for (let z = 0; z < CHUNK_SIZE; z++) {
        for (let x = 0; x < CHUNK_SIZE; x++) {
          const blockId = chunk.getBlock(x, y, z);

          if (blockId === Block.AIR) continue;

          const blockDef = this.registry.get(blockId);
          if (!blockDef) continue;

          // Check each face
          for (const face of this.FACES) {
            const [dx, dy, dz] = face.dir;
            const neighborBlock = getBlock(x + dx, y + dy, z + dz);

            // Only render face if neighbor is transparent (or air)
            if (Block.isTransparent(neighborBlock) && neighborBlock !== blockId) {
              this.addFace(
                faceData,
                x, y, z,
                face.name,
                blockDef.color,
                blockDef.transparent ? 0.7 : 1.0,
                vertexCount
              );
              vertexCount += 4;
            }
          }
        }
      }
    }

    if (faceData.positions.length === 0) {
      return null;
    }

    return this.createMeshFromData(chunk, faceData);
  }

  private static addFace(
    data: FaceData,
    x: number, y: number, z: number,
    faceName: string,
    color: { r: number; g: number; b: number },
    alpha: number,
    baseVertex: number
  ): void {
    const vertices = this.FACE_VERTICES[faceName];
    const normal = this.FACE_NORMALS[faceName];

    // Add slight ambient occlusion variation based on position
    const ao = this.calculateAO(faceName, x, y, z);

    // Add vertices
    for (let i = 0; i < 4; i++) {
      const [vx, vy, vz] = vertices[i];
      data.positions.push(x + vx, y + vy, z + vz);
      data.normals.push(normal[0], normal[1], normal[2]);

      // Apply ambient occlusion to vertex color
      const aoFactor = 0.7 + 0.3 * ao[i];
      data.colors.push(
        color.r * aoFactor,
        color.g * aoFactor,
        color.b * aoFactor,
        alpha
      );
    }

    // Add indices (two triangles per face)
    data.indices.push(
      baseVertex, baseVertex + 1, baseVertex + 2,
      baseVertex, baseVertex + 2, baseVertex + 3
    );
  }

  private static calculateAO(faceName: string, _x: number, y: number, _z: number): number[] {
    // Simple ambient occlusion based on face direction and height
    // Returns 4 values for each vertex corner
    const baseAO = Math.min(1, 0.5 + y / CHUNK_HEIGHT * 0.5);

    if (faceName === 'top') {
      return [1, 1, 1, 1];
    } else if (faceName === 'bottom') {
      return [0.6, 0.6, 0.6, 0.6];
    } else {
      // Side faces: darker at bottom
      return [
        baseAO * 0.85,
        baseAO * 0.85,
        baseAO,
        baseAO
      ];
    }
  }

  private static createMeshFromData(chunk: Chunk, data: FaceData): Mesh {
    const mesh = new Mesh(`chunk_${chunk.coord.x}_${chunk.coord.z}`, chunk.getScene());

    const vertexData = new VertexData();
    vertexData.positions = data.positions;
    vertexData.normals = data.normals;
    vertexData.indices = data.indices;
    vertexData.colors = data.colors;

    vertexData.applyToMesh(mesh);

    // Position mesh in world
    mesh.position = chunk.worldPosition.clone();

    // Create material with vertex colors
    const material = new StandardMaterial(`mat_chunk_${chunk.coord.x}_${chunk.coord.z}`, chunk.getScene());
    material.specularColor.set(0.1, 0.1, 0.1);
    material.backFaceCulling = true;
    material.diffuseColor.set(1, 1, 1); // Let vertex colors show through

    mesh.material = material;

    // Enable shadows and collisions
    mesh.receiveShadows = true;
    mesh.checkCollisions = true;

    return mesh;
  }

  public static disposeMesh(mesh: Mesh): void {
    if (mesh.material) {
      mesh.material.dispose();
    }
    mesh.dispose();
  }
}
